import React, { useState } from 'react';
import { SessionState, MaterializedView, AccountRecord } from '@/types';
import { api } from '@/services/api';
import {
    AccountBalance,
    Business,
    PersonOutline,
    Assignment,
    History,
    CheckCircle,
    ChevronRight,
    WarningAmber
} from '@mui/icons-material';

interface AccountHistoryViewProps {
    session: SessionState;
    materializedView: MaterializedView;
}

const STATUS_TONE: Record<string, string> = {
    'active': 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40',
    'at-risk': 'text-rose-300 bg-rose-500/15 border-rose-500/40',
    'renewal-pending': 'text-amber-300 bg-amber-500/15 border-amber-500/40',
    'complaint': 'text-rose-300 bg-rose-500/15 border-rose-500/40',
    'prospect': 'text-sky-300 bg-sky-500/15 border-sky-500/40'
};

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
    'key-account': 'Key Account',
    'prospect': 'Prospect',
    'complaint': 'Complaint Pending',
    'standard': 'Standard'
};

const AccountHistoryView: React.FC<AccountHistoryViewProps> = ({ session, materializedView }) => {
    const records = Object.values(materializedView.accountRecords || {});
    const [selectedId, setSelectedId] = useState<string | null>(records[0]?.id ?? null);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [localViewed, setLocalViewed] = useState<Record<string, boolean>>({});

    const handleSelect = async (record: AccountRecord) => {
        setSelectedId(record.id);
        if (record.hasBeenViewed || localViewed[record.id]) return;

        setPendingId(record.id);
        setLocalViewed(prev => ({ ...prev, [record.id]: true }));
        try {
            await api.viewAccountRecord(session.id, record.id);
        } catch (err) {
            console.error('Failed to record account view', err);
        } finally {
            setPendingId(null);
        }
    };

    const selected = records.find(r => r.id === selectedId) ?? null;
    const unviewedCount = records.filter(r => !(r.hasBeenViewed || localViewed[r.id])).length;

    if (records.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-slate-800 text-slate-400">
                <AccountBalance style={{ fontSize: 56 }} className="mb-3 text-slate-500" />
                <p className="text-sm">No account records inherited.</p>
            </div>
        );
    }

    return (
        <div className="flex h-full bg-slate-800 text-white">
            {/* Header bar */}
            <div className="absolute top-0 left-0 right-0 px-6 py-4 border-b border-slate-600 bg-slate-800 z-10">
                <div className="flex items-center gap-3">
                    <AccountBalance className="text-amber-400" />
                    <div>
                        <h2 className="text-lg font-bold text-white">Account History</h2>
                        <p className="text-xs text-slate-300">
                            {unviewedCount > 0
                                ? `${unviewedCount} record${unviewedCount > 1 ? 's' : ''} not yet reviewed`
                                : 'All records reviewed'}
                        </p>
                    </div>
                    {unviewedCount > 0 && (
                        <span className="ml-auto text-xs font-bold bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
                            {unviewedCount}
                        </span>
                    )}
                </div>
            </div>

            {/* List rail */}
            <aside className="w-72 shrink-0 border-r border-slate-600 bg-slate-900 overflow-y-auto pt-[72px]">
                <ul className="divide-y divide-slate-700/60">
                    {records.map((rec) => {
                        const isViewed = rec.hasBeenViewed || localViewed[rec.id];
                        const isSelected = rec.id === selectedId;
                        return (
                            <li key={rec.id}>
                                <button
                                    onClick={() => handleSelect(rec)}
                                    className={`w-full text-left px-5 py-4 transition-colors group ${isSelected ? 'bg-slate-800 border-l-2 border-amber-400' : 'border-l-2 border-transparent hover:bg-slate-800/60'}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`text-sm font-bold truncate ${isSelected ? 'text-white' : 'text-slate-200'}`}>{rec.accountName}</span>
                                                {!isViewed && (
                                                    <span className="size-2 rounded-full bg-amber-400 shrink-0" title="Not yet reviewed" />
                                                )}
                                                {isViewed && (
                                                    <CheckCircle style={{ fontSize: 12 }} className="text-amber-400/60 shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-400 mt-0.5">
                                                {ACCOUNT_TYPE_LABEL[rec.accountType] ?? rec.accountType}
                                                {rec.industry && ` · ${rec.industry}`}
                                            </p>
                                        </div>
                                        <ChevronRight fontSize="small" className="text-slate-500 group-hover:text-slate-300 transition-colors" />
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </aside>

            {/* Detail pane */}
            <main className="flex-1 overflow-y-auto pt-[72px] bg-slate-800">
                {selected && (
                    <article className="max-w-3xl mx-auto p-6">
                        {/* Title block */}
                        <div className="border-b border-slate-600 pb-4 mb-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <h1 className="text-2xl font-bold text-white leading-tight">{selected.accountName}</h1>
                                    <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-1">
                                        {ACCOUNT_TYPE_LABEL[selected.accountType] ?? selected.accountType}
                                        {selected.industry && ` · ${selected.industry}`}
                                    </p>
                                </div>
                                {selected.status && (
                                    <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded border ${STATUS_TONE[selected.status] ?? 'text-slate-300 bg-slate-700/40 border-slate-600'}`}>
                                        {selected.status.replace('-', ' ')}
                                    </span>
                                )}
                            </div>
                            {pendingId === selected.id && (
                                <p className="text-[10px] text-amber-400 mt-2 animate-pulse">Recording review…</p>
                            )}
                        </div>

                        {/* Metadata grid */}
                        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm mb-6 bg-slate-900/40 border border-slate-700 rounded-lg p-4">
                            {selected.relationshipOwner && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider text-slate-400">Relationship Owner</dt>
                                    <dd className="text-slate-100 font-medium">{selected.relationshipOwner}</dd>
                                </div>
                            )}
                            {selected.contractValue && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider text-slate-400">Contract Value</dt>
                                    <dd className="text-slate-100 font-medium">{selected.contractValue}</dd>
                                </div>
                            )}
                            {selected.contractRenewal && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider text-slate-400">Renewal</dt>
                                    <dd className="text-slate-100 font-medium">{selected.contractRenewal}</dd>
                                </div>
                            )}
                        </dl>

                        {selected.summary && (
                            <section className="mb-6">
                                <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-2"><Business fontSize="small" /> Summary</h3>
                                <p className="text-slate-200 leading-relaxed">{selected.summary}</p>
                            </section>
                        )}

                        {selected.keyContacts && selected.keyContacts.length > 0 && (
                            <section className="mb-6">
                                <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-2"><PersonOutline fontSize="small" /> Key Contacts</h3>
                                <ul className="space-y-2">
                                    {selected.keyContacts.map((c, i) => (
                                        <li key={i} className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5">
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="font-bold text-white">{c.name}</span>
                                                <span className="text-[11px] text-slate-400">{c.email}</span>
                                            </div>
                                            <p className="text-xs text-slate-400 mt-0.5">{c.title}</p>
                                            {c.notes && <p className="text-xs text-slate-300 italic mt-1">{c.notes}</p>}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {selected.contractTerms && selected.contractTerms.length > 0 && (
                            <section className="mb-6">
                                <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-2"><Assignment fontSize="small" /> Contract Terms</h3>
                                <ul className="list-disc list-inside text-sm text-slate-200 space-y-1 marker:text-slate-500 bg-slate-900/40 border border-slate-700 rounded-lg p-4">
                                    {selected.contractTerms.map((t, i) => <li key={i}>{t}</li>)}
                                </ul>
                            </section>
                        )}

                        {selected.openItems && selected.openItems.length > 0 && (
                            <section className="mb-6">
                                <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-2"><WarningAmber fontSize="small" /> Open Items</h3>
                                <ul className="space-y-1.5">
                                    {selected.openItems.map((item, i) => (
                                        <li key={i} className="text-sm text-slate-100 bg-amber-500/10 border-l-2 border-amber-500/60 px-3 py-2 rounded-r">{item}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {selected.recentActivity && selected.recentActivity.length > 0 && (
                            <section>
                                <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-2"><History fontSize="small" /> Recent Activity</h3>
                                <ol className="border-l border-slate-600 pl-4 space-y-3">
                                    {selected.recentActivity.map((act, i) => (
                                        <li key={i} className="relative">
                                            <span className="absolute -left-[1.18rem] top-1.5 size-2 rounded-full bg-amber-400/80 border-2 border-slate-800" />
                                            <div className="text-[10px] uppercase tracking-wider text-slate-400">{act.date} · {act.type}</div>
                                            <p className="text-sm text-slate-200">{act.summary}</p>
                                        </li>
                                    ))}
                                </ol>
                            </section>
                        )}
                    </article>
                )}
            </main>
        </div>
    );
};

export default AccountHistoryView;
